/**
 * PanelToggle — a standalone show/hide button for a side panel.
 *
 * Decoupled from both the panel and the board: it's just a controlled toggle a
 * host drops wherever it wants (e.g. the viewer's board chrome) and wires to a
 * `ResizablePanel`'s `collapsed`/`onCollapsedChange`. Styled as a HUD pill so
 * it reads when overlaid on the board.
 */
import type { ReactElement } from "react";
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { IconButton, Tooltip } from "@archlang/ui";

export interface PanelToggleProps {
  /** Which side panel this controls (picks the icon orientation). */
  readonly side: "left" | "right";
  readonly collapsed: boolean;
  readonly onToggle: () => void;
  /** Accessible name, e.g. "navigator" / "details". */
  readonly label: string;
  readonly className?: string;
  /** "hud" = floating pill over the board (default); "strip" = flat button for
   *  the tab strip's ends (no border/blur/shadow). */
  readonly variant?: "hud" | "strip";
}

export function PanelToggle({
  side,
  collapsed,
  onToggle,
  label,
  className,
  variant = "hud",
}: PanelToggleProps): ReactElement {
  const Icon =
    side === "left"
      ? collapsed
        ? PanelLeftOpen
        : PanelLeftClose
      : collapsed
        ? PanelRightOpen
        : PanelRightClose;
  // Only the HUD skin (border + blur + shadow) and pointer-events survive —
  // size, rounding, colours, hover and the focus ring come from IconButton.
  const skin =
    variant === "strip"
      ? ""
      : "border border-slate-700 bg-slate-900/(--tint-strong) shadow-lg backdrop-blur-md";
  const name = `${collapsed ? "Show" : "Hide"} ${label}`;
  return (
    <Tooltip content={name} placement={side === "left" ? "right" : "left"}>
      <IconButton
        onClick={onToggle}
        aria-label={name}
        aria-pressed={!collapsed}
        className={`pointer-events-auto ${skin} ${className ?? ""}`}
      >
        <Icon className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
      </IconButton>
    </Tooltip>
  );
}
